// Communications (Phase 1) — pure helpers shared by the Account →
// Communications tab and the /admin/communications page.
//
// Timestamps from the messages tables are SQLite 'YYYY-MM-DD HH:MM:SS' in
// UTC (no zone suffix). Interactions use the same shape. Everything is shown
// in America/New_York like the rest of the admin.

const NY_TZ = 'America/New_York';

// 'YYYY-MM-DD HH:MM:SS' (UTC) | ISO | Date → Date, or null when unparseable.
export function parseTs(v) {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.valueOf()) ? null : v;
  if (typeof v === 'number') return Number.isNaN(v) ? null : new Date(v);
  let s = String(v).trim();
  // Date-only values (e.g. backfillAfter) carry no zone; pin them to noon UTC
  // so the NY calendar day matches the stored day instead of slipping back.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) s += 'T12:00:00Z';
  else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(s)) s = s.replace(' ', 'T') + 'Z';
  else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s)) s += 'Z';
  const d = new Date(s);
  return Number.isNaN(d.valueOf()) ? null : d;
}

function nyParts(d) {
  const f = new Intl.DateTimeFormat('en-US', {
    timeZone: NY_TZ, year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
  const parts = Object.fromEntries(f.formatToParts(d).map((p) => [p.type, p.value]));
  return parts;
}

function nyDayKey(d) {
  return d.toLocaleDateString('en-CA', { timeZone: NY_TZ });
}

// "3 min ago", "2 hours ago", "Yesterday", "4 days ago", "Jun 24".
export function relativeTime(v, now = new Date(Date.now())) {
  const d = parseTs(v);
  if (!d) return '';
  const diffMs = now.valueOf() - d.valueOf();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24 && nyDayKey(d) === nyDayKey(now)) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(diffMs / 86400000);
  const yesterday = new Date(now.valueOf() - 86400000);
  if (nyDayKey(d) === nyDayKey(yesterday)) return 'Yesterday';
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const p = nyParts(d);
  const sameYear = p.year === nyParts(now).year;
  return sameYear ? `${p.month} ${p.day}` : `${p.month} ${p.day}, ${p.year}`;
}

// "Today 4:12 PM" / "Yesterday 4:12 PM" / "Sep 9, 2:30 PM" / "Jun 24, 2025".
export function messageTime(v, now = new Date(Date.now())) {
  const d = parseTs(v);
  if (!d) return '';
  const p = nyParts(d);
  const time = `${p.hour}:${p.minute} ${p.dayPeriod}`;
  const key = nyDayKey(d);
  if (key === nyDayKey(now)) return `Today ${time}`;
  if (key === nyDayKey(new Date(now.valueOf() - 86400000))) return `Yesterday ${time}`;
  if (p.year !== nyParts(now).year) return `${p.month} ${p.day}, ${p.year}`;
  return `${p.month} ${p.day}, ${time}`;
}

// "Oct 1" / "Oct 1, 2027" — for dates that aren't about recency.
export function shortDate(v, now = new Date(Date.now())) {
  const d = parseTs(v);
  if (!d) return '';
  const p = nyParts(d);
  return p.year === nyParts(now).year ? `${p.month} ${p.day}` : `${p.month} ${p.day}, ${p.year}`;
}

// Same key used for sorting merged lists — numeric ms, 0 for unknown.
export function tsValue(v) {
  const d = parseTs(v);
  return d ? d.valueOf() : 0;
}

// "Husain Yaseen" → "HY"; "support@nexsav.net" → "S".
export function initials(nameOrEmail) {
  const s = String(nameOrEmail || '').trim();
  if (!s) return '?';
  const name = s.includes('@') && !s.includes(' ') ? s.split('@')[0].replace(/[._-]+/g, ' ') : s;
  const words = name.replace(/[<>"]/g, '').split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

export function firstName(name) {
  const s = String(name || '').trim();
  if (!s) return '';
  return s.split(/\s+/)[0];
}

// "Carmen Sanchez" → "C. Sanchez"; single word stays; email → local part.
export function shortName(name, email) {
  const s = String(name || '').trim();
  if (!s) return email ? String(email).split('@')[0] : '';
  const words = s.split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0];
  return `${words[0][0]}. ${words[words.length - 1]}`;
}

// Deterministic avatar tone per name so the same person always gets the
// same colour across rows.
const AVATAR_TONES = [
  'bg-[#1f2a3a] text-[#9ec5ff]',
  'bg-[#2a2617] text-[#f1d38a]',
  'bg-[#1e2a22] text-[#9fe4b0]',
  'bg-[#2a1e2a] text-[#e4a2e0]',
  'bg-[#2a1f1a] text-[#f0b48f]',
  'bg-[#1f2a2a] text-[#9fe0d8]',
];
export function avatarTone(key) {
  const s = String(key || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

export const CONTACT_ROLE_LABELS = {
  principal: 'Principal',
  assistant_principal: 'Assistant principal',
  parent_coordinator: 'Parent coordinator',
  purchase_secretary: 'Purchasing secretary',
  it_staff: 'IT staff',
  superintendent: 'Superintendent',
  other_school: 'Other (school)',
};
export const CONTACT_ROLE_OPTIONS = Object.entries(CONTACT_ROLE_LABELS).map(([value, label]) => ({ value, label }));
export function contactRoleLabel(role) {
  return CONTACT_ROLE_LABELS[role] || (role ? String(role).replace(/_/g, ' ') : '');
}

export const CALL_VISIT_CHANNELS = new Set(['phone', 'in_person', 'sms', 'video_call']);
export const CHANNEL_LABELS = {
  phone: 'Phone call',
  in_person: 'School visit',
  sms: 'Text message',
  video_call: 'Video call',
  email: 'Email',
  other: 'Other',
};
export function channelLabel(ch) {
  return CHANNEL_LABELS[ch] || (ch ? String(ch).replace(/_/g, ' ') : 'Interaction');
}

export const AUTOMATED_SOURCES = new Set(['outreach', 'notification']);
export function isAutomatedMessage(m) {
  return !!m && AUTOMATED_SOURCES.has(m.source);
}
export function isAutomatedConversation(c) {
  return (c?.messages || []).some(isAutomatedMessage);
}

// Local-part chip: "support@nexsav.net" → "support@".
export function localPart(email) {
  const s = String(email || '');
  if (!s) return '';
  const i = s.indexOf('@');
  return i === -1 ? s : `${s.slice(0, i)}@`;
}

export function isNexsavAddress(email) {
  return /@nexsav\.net$/i.test(String(email || ''));
}

// The @nexsav.net address a message went through: from-address for outbound,
// the first nexsav recipient for inbound. Falls back to the mailbox itself.
export function nexsavAddressFor(m) {
  if (!m) return '';
  if (m.direction === 'outbound' && isNexsavAddress(m.from?.email)) return m.from.email;
  const p = (m.participants || []).find((x) => x.kind !== 'from' && isNexsavAddress(x.email));
  if (p) return p.email;
  if (isNexsavAddress(m.from?.email)) return m.from.email;
  return (m.mailboxes || [])[0] || '';
}

// When the address used isn't the mailbox's own address, it was an alias.
// Returns { alias, mailbox } or null.
export function aliasInfo(m) {
  const used = nexsavAddressFor(m);
  const boxes = (m?.mailboxes || []).map((b) => String(b).toLowerCase());
  if (!used || boxes.length === 0) return null;
  if (boxes.includes(used.toLowerCase())) return null;
  return { alias: used, mailbox: boxes[0] };
}

// "support@ · Husain" — mailbox + the person behind it, for the row chip.
export function mailboxChip(conv) {
  const msgs = conv?.messages || [];
  if (msgs.length === 0) return '';
  const lastOut = [...msgs].reverse().find((m) => m.direction === 'outbound');
  const m = lastOut || msgs[msgs.length - 1];
  const addr = localPart(nexsavAddressFor(m));
  let who = firstName(m.staffName) || firstName(m.sentByName);
  if (!who && isAutomatedMessage(m)) who = m.source === 'notification' ? 'Portal' : 'Automated';
  if (!who && m.source === 'gmail') who = 'Gmail';
  return [addr, who].filter(Boolean).join(' · ');
}

// ---------------------------------------------------------------------------
// Quoted-reply detection. Mail clients append the whole earlier thread under
// a marker line; the thread view hides that tail behind "Show quoted text".
// Rules are tried in priority order (most reliable marker first), and the
// first rule that matches wins — a stray ">" run never trumps a real
// "On … wrote:" line further down.
//
//   1. "On Sun, Sep 13, 2026 at 9:19 AM Name <a@b> wrote:" (may wrap once)
//   2. "-----Original Message-----" / "---- Forwarded Message ----"
//   3. Outlook header block: "From: …" followed within 4 lines by
//      "Sent:"/"Date:" and "To:"
//   4. the first run of ≥2 consecutive ">"-prefixed lines
//
// Returns { visible, quoted } with trailing whitespace trimmed; `quoted` is
// '' when nothing is detected — or when the marker is the very first thing in
// the body (a forward with no note on top), since hiding everything helps
// nobody.

const ON_WROTE_RE = /^On .{5,200} wrote:\s*$/;
const ORIGINAL_MESSAGE_RE = /^-{2,}\s*(Original|Forwarded) Message\s*-{2,}$/i;
const OUTLOOK_FROM_RE = /^From: .+$/;
const OUTLOOK_SENT_RE = /^(Sent|Date): /;
const OUTLOOK_TO_RE = /^To: /;
const QUOTE_LINE_RE = /^>/;
const RULE_LINE_RE = /^[_-]{3,}\s*$/;

function findOnWrote(lines) {
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimEnd();
    if (ON_WROTE_RE.test(line)) return i;
    // Wrapped: "On Sun, … <a@b>" / "wrote:" — join with the next line.
    if (/^On .{5,200}$/.test(line) && !/wrote:\s*$/.test(line) && i + 1 < lines.length) {
      const joined = `${line} ${lines[i + 1].trim()}`;
      if (ON_WROTE_RE.test(joined)) return i;
    }
  }
  return -1;
}

function findOriginalMessage(lines) {
  return lines.findIndex((l) => ORIGINAL_MESSAGE_RE.test(l.trim()));
}

function findOutlookHeader(lines) {
  for (let i = 0; i < lines.length; i++) {
    if (!OUTLOOK_FROM_RE.test(lines[i].trimEnd())) continue;
    const window = lines.slice(i + 1, i + 5).map((l) => l.trimEnd());
    if (window.some((l) => OUTLOOK_SENT_RE.test(l)) && window.some((l) => OUTLOOK_TO_RE.test(l))) return i;
  }
  return -1;
}

function findQuoteRun(lines) {
  for (let i = 0; i + 1 < lines.length; i++) {
    if (QUOTE_LINE_RE.test(lines[i]) && QUOTE_LINE_RE.test(lines[i + 1])) return i;
  }
  return -1;
}

export function splitQuotedText(bodyText) {
  const text = String(bodyText || '');
  const lines = text.split(/\r?\n/);
  let start = -1;
  for (const find of [findOnWrote, findOriginalMessage, findOutlookHeader, findQuoteRun]) {
    start = find(lines);
    if (start !== -1) break;
  }
  if (start === -1) return { visible: text.trimEnd(), quoted: '' };
  // Outlook and some clients put a "____" rule right above the header — keep
  // it with the quote rather than leaving a stray line in the visible part.
  let from = start;
  while (from > 0 && lines[from - 1].trim() === '') from -= 1;
  if (from > 0 && RULE_LINE_RE.test(lines[from - 1])) from -= 1;
  const visible = lines.slice(0, from).join('\n').trimEnd();
  const quoted = lines.slice(start).join('\n').trimEnd();
  if (!visible || !quoted) return { visible: text.trimEnd(), quoted: '' };
  return { visible, quoted };
}

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Merge conversations and timeline interactions into one newest-first list.
// Only call/visit-style interactions are included (phone, in person, sms,
// video), never stage transitions or system rows.
export function buildFeed(conversations = [], timeline = []) {
  const items = [];
  for (const c of conversations) {
    items.push({ kind: 'conversation', id: `c-${c.id}`, at: tsValue(c.lastMessageAt), conversation: c });
  }
  for (const e of timeline) {
    if (e?.kind !== 'interaction' || !e.interaction) continue;
    const it = e.interaction;
    if (it.type === 'system') continue;
    if (it.stageFrom && it.stageTo) continue;
    if (!CALL_VISIT_CHANNELS.has(it.channel)) continue;
    items.push({ kind: 'interaction', id: `i-${it.id}`, at: tsValue(e.at || it.occurredAt), interaction: it });
  }
  items.sort((a, b) => b.at - a.at);
  return items;
}

export const FEED_FILTERS = [
  ['all', 'All'],
  ['needs_reply', 'Needs reply'],
  ['emails', 'Emails'],
  ['calls', 'Calls & visits'],
  ['automated', 'Automated'],
];

export function feedMatches(item, filter) {
  switch (filter) {
    case 'needs_reply': return item.kind === 'conversation' && !!item.conversation.needsReply;
    case 'emails': return item.kind === 'conversation';
    case 'calls': return item.kind === 'interaction';
    case 'automated': return item.kind === 'conversation' && isAutomatedConversation(item.conversation);
    default: return true;
  }
}

export function feedCounts(items) {
  const counts = {};
  for (const [key] of FEED_FILTERS) counts[key] = items.filter((i) => feedMatches(i, key)).length;
  return counts;
}

// Flash copy for ?google=<status> after the OAuth round-trip.
export const GOOGLE_FLASH = {
  connected: { tone: 'lime', text: 'Gmail connected. Your history is importing in the background.' },
  cancelled: { tone: 'grey', text: 'Google sign-in was cancelled. Nothing was changed.' },
  wrong_domain: { tone: 'red', text: 'Only @nexsav.net Google accounts can be connected.' },
  mailbox_mismatch: { tone: 'red', text: 'That is a different Google account from the mailbox already connected to your login. Disconnect the old one first if you mean to switch.' },
  bad_state: { tone: 'red', text: 'That Google sign-in link was invalid or had already been used. Start again from this page.' },
  forbidden: { tone: 'red', text: 'Only admin and ops staff can connect a mailbox.' },
  exchange_failed: { tone: 'red', text: 'Google didn’t complete the sign-in. Try connecting again.' },
  no_refresh: { tone: 'red', text: 'Google didn’t grant offline access. Remove NexSav from your Google account’s third-party access and connect again.' },
  store_failed: { tone: 'red', text: 'Connected to Google, but saving the mailbox failed. Try again.' },
  not_configured: { tone: 'amber', text: 'Google sign-in isn’t configured on this environment.' },
  session_lost: { tone: 'amber', text: 'Your session expired during Google sign-in. Sign in and try again.' },
};

// ---------------------------------------------------------------------------
// Phase 2 — reply / compose helpers. Pure, so the composer and the Needs-reply
// page can share them and the tests stay cheap.

export function isAutomaticMessage(m) {
  return !!m?.isAutomatic;
}

// Latest message a real person at the school sent (not an out-of-office /
// auto-ack). Falls back to the latest inbound when nothing is flagged.
export function latestHumanInbound(conv) {
  const msgs = conv?.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) {
    const m = msgs[i];
    if (m.direction === 'inbound' && !isAutomaticMessage(m)) return m;
  }
  for (let i = msgs.length - 1; i >= 0; i--) if (msgs[i].direction === 'inbound') return msgs[i];
  return null;
}

export function latestOutbound(conv) {
  const msgs = conv?.messages || [];
  for (let i = msgs.length - 1; i >= 0; i--) if (msgs[i].direction === 'outbound') return msgs[i];
  return null;
}

// Recipient shape used by the composer chips: { email, name, role }.
export function recipientFromParticipant(p) {
  if (!p) return null;
  const name = p.contactName || p.name || '';
  const role = p.contactRoleTitle || (p.contactRole ? contactRoleLabel(p.contactRole) : '');
  return { email: String(p.email || '').toLowerCase(), name, role, contactId: p.contactId || null };
}

function dedupeRecipients(list) {
  const seen = new Set();
  const out = [];
  for (const r of list) {
    if (!r || !r.email || seen.has(r.email)) continue;
    seen.add(r.email);
    out.push(r);
  }
  return out;
}

// Same default the server applies when `to` is omitted: the school-side people
// on the most recent message — inbound → its sender, outbound → its To's —
// never anyone @nexsav.net. Cc = the school-side Cc's on that message.
export function defaultReplyRecipients(conv) {
  const msgs = conv?.messages || [];
  const last = msgs[msgs.length - 1];
  if (!last) return { to: [], cc: [] };
  const ext = (last.participants || []).filter((p) => p.email && !isNexsavAddress(p.email));
  let to = ext.filter((p) => (last.direction === 'inbound' ? p.kind === 'from' : p.kind === 'to'));
  if (to.length === 0 && last.direction === 'inbound' && last.from?.email && !isNexsavAddress(last.from.email)) {
    to = [{ kind: 'from', email: last.from.email, name: last.from.name }];
  }
  if (to.length === 0) to = ext.filter((p) => p.kind !== 'cc');
  const cc = ext.filter((p) => p.kind === 'cc');
  const toList = dedupeRecipients(to.map(recipientFromParticipant));
  const toEmails = new Set(toList.map((r) => r.email));
  return {
    to: toList,
    cc: dedupeRecipients(cc.map(recipientFromParticipant)).filter((r) => !toEmails.has(r.email)),
  };
}

// The From picker groups one entry per mailbox ("Your mailbox · ammar@" first)
// with one option per Send-as alias. Options carry `allowed` + `reason` so the
// picker can grey them with a tag.
export function groupSenders(senders = [], { threadFrom = null } = {}) {
  const thread = String(threadFrom || '').toLowerCase();
  const sorted = [...senders].sort((a, b) => (a.isMine === b.isMine ? 0 : a.isMine ? -1 : 1));
  return sorted.map((s) => {
    const owner = firstName(s.ownerName) || s.ownerName || s.email;
    const aliases = s.sendAs && s.sendAs.length > 0 ? s.sendAs : [{ email: s.email, name: s.ownerName }];
    const allowed = !!s.allowed && s.status === 'connected';
    const reason = !s.allowed ? s.reason : (s.status !== 'connected' ? 'mailbox_not_connected' : s.reason);
    return {
      mailboxId: s.mailboxId,
      ownerUserId: s.ownerUserId,
      ownerName: s.ownerName,
      email: s.email,
      isMine: !!s.isMine,
      allowed,
      reason,
      label: s.isMine ? `Your mailbox · ${s.email}` : `${owner}’s mailbox · ${s.email}`,
      options: aliases.map((a) => {
        const email = String(a.email || '').toLowerCase();
        const isOwnAddress = email === String(s.email).toLowerCase();
        let tag;
        if (!allowed) tag = reason === 'mailbox_not_connected' || reason === 'not_connected' ? 'not connected' : 'not allowed';
        else if (email === thread) tag = isOwnAddress ? 'this thread' : 'alias · this thread';
        else if (s.isMine) tag = isOwnAddress ? 'you' : 'alias';
        else tag = isOwnAddress ? 'allowed' : 'alias';
        return {
          value: email,
          email,
          name: a.name || s.ownerName || email,
          mailboxId: s.mailboxId,
          ownerName: s.ownerName,
          isMine: !!s.isMine,
          isDefault: !!a.is_default || (isOwnAddress && !aliases.some((x) => x.is_default)),
          // The alias's Gmail signature HTML (null when none is set). The
          // server appends it on send; the composer only previews it.
          signature: typeof a.signature === 'string' && a.signature.trim() ? a.signature : null,
          allowed,
          reason,
          tag,
        };
      }),
    };
  });
}

export function flattenSenderOptions(groups) {
  return groups.flatMap((g) => g.options);
}

// The same alias can be set up on two people's Gmail; `mailboxId` says which
// one was meant. Without it, the first match wins (own mailbox sorts first).
export function findSenderOption(groups, email, mailboxId = null) {
  const e = String(email || '').toLowerCase();
  const all = flattenSenderOptions(groups).filter((o) => o.value === e);
  if (!all.length) return null;
  return (mailboxId && all.find((o) => o.mailboxId === mailboxId)) || all[0];
}

// Default From: when replying, the alias the thread lives in (the From of the
// latest outbound) if the user may use it; else the user's own default alias;
// else the first allowed option anywhere.
export function defaultFromEmail(groups, { conversation = null } = {}) {
  const opts = flattenSenderOptions(groups).filter((o) => o.allowed);
  if (opts.length === 0) return '';
  if (conversation) {
    const lastOut = latestOutbound(conversation);
    const threadFrom = String(lastOut?.from?.email || '').toLowerCase();
    if (threadFrom) {
      const hit = opts.find((o) => o.value === threadFrom);
      if (hit) return hit.value;
    }
    // Inbound-only thread: the alias the school wrote to.
    const lastIn = latestHumanInbound(conversation);
    const wroteTo = String(nexsavAddressFor(lastIn) || '').toLowerCase();
    if (wroteTo) {
      const hit = opts.find((o) => o.value === wroteTo);
      if (hit) return hit.value;
    }
  }
  const mine = opts.filter((o) => o.isMine);
  const own = mine.find((o) => o.isDefault) || mine[0];
  return (own || opts[0]).value;
}

// "sent from support@ by Ammar"
export function recordedAs(fromEmail, userName) {
  const from = localPart(fromEmail) || 'the chosen mailbox';
  const who = firstName(userName) || userName || 'you';
  return `sent from ${from} by ${who}`;
}

// "Waiting 6h" / "Waiting 1d" / "Waiting 12m"; overdue past three days.
export const NEEDS_REPLY_OVERDUE_MS = 3 * 86400000;
export function waitingFor(v, now = new Date(Date.now())) {
  const d = parseTs(v);
  if (!d) return { label: '', overdue: false, ms: 0 };
  const ms = Math.max(0, now.valueOf() - d.valueOf());
  const mins = Math.floor(ms / 60000);
  let label;
  if (mins < 60) label = `Waiting ${Math.max(mins, 1)}m`;
  else if (mins < 60 * 24) label = `Waiting ${Math.floor(mins / 60)}h`;
  else label = `Waiting ${Math.floor(mins / (60 * 24))}d`;
  return { label, overdue: ms > NEEDS_REPLY_OVERDUE_MS, ms };
}

// Resolved wins only when it happened after the school's latest human message.
export function isResolved(conv) {
  if (!conv?.resolvedAt) return false;
  const r = tsValue(conv.resolvedAt);
  const h = tsValue(conv.lastHumanInboundAt);
  return !h || r >= h;
}

const EMAIL_RE = /^[^\s@<>,;"']+@[^\s@<>,;"']+\.[^\s@<>,;"']+$/;
export function isValidEmail(s) {
  return EMAIL_RE.test(String(s || '').trim());
}

// "Carmen <c@x.org>, d@y.org; e@z.org" → [{email,name}]; invalid entries returned separately.
export function parseRecipientInput(text) {
  const out = [];
  const invalid = [];
  for (const raw of String(text || '').split(/[,;\n]+/)) {
    const part = raw.trim();
    if (!part) continue;
    const m = part.match(/^(.*?)\s*<([^>]+)>$/);
    const email = (m ? m[2] : part).trim().toLowerCase();
    const name = m ? m[1].replace(/^"|"$/g, '').trim() : '';
    if (isValidEmail(email)) out.push({ email, name, role: '' });
    else invalid.push(part);
  }
  return { recipients: out, invalid };
}

export function htmlToPlainText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function isBodyEmpty(html) {
  return htmlToPlainText(html).length === 0;
}

export function replySubjectFor(subject) {
  const s = String(subject || '').trim();
  if (!s) return 'Re:';
  return /^re:/i.test(s) ? s : `Re: ${s}`;
}

// Copy for a failed send, keyed by the server's error code. `err` is an
// ApiError (message/code/status/data) or anything with a message.
export function sendErrorCopy(err, { fromEmail = '' } = {}) {
  const code = err?.code || err?.data?.code;
  const job = err?.data?.job;
  switch (code) {
    case 'suppressed': {
      const emails = err?.data?.emails || [];
      return {
        code,
        emails,
        title: emails.length === 1 ? `${emails[0]} can’t be emailed` : 'Some recipients can’t be emailed',
        detail: emails.length
          ? `${emails.join(', ')} ${emails.length === 1 ? 'is' : 'are'} on the suppression list (bounced, complained or unsubscribed). Remove ${emails.length === 1 ? 'it' : 'them'} to send.`
          : err.message,
        retryable: false,
      };
    }
    case 'revoked':
      return { code, title: `You’re not allowed to send from ${fromEmail || 'that mailbox'}`, detail: 'Pick a different From address, or ask an admin to grant access on the Mailboxes page.', retryable: false };
    case 'mailbox_not_connected':
    case 'not_connected':
      return { code, title: `${fromEmail || 'That mailbox'} isn’t connected`, detail: 'Its owner needs to connect Gmail before anyone can send from it.', retryable: false };
    case 'unknown_sender':
      return { code, title: 'Choose a From address', detail: err?.message || 'That address isn’t a connected mailbox or one of its Send-as aliases.', retryable: false };
    case 'opted_out':
      return { code, title: 'This school opted out', detail: err?.message || 'The school opted out of email from NexSav, so nothing was sent.', retryable: false };
    case 'suppression_check_failed':
      return { code, title: 'Couldn’t check the suppression list', detail: 'Nothing was sent. Try again in a moment.', retryable: true };
    case 'send_failed':
      return { code, title: 'Gmail didn’t accept the message', detail: job?.lastError || err?.message || 'Nothing was sent.', retryable: true };
    default:
      if (job?.status === 'failed') {
        return { code: 'send_failed', title: 'Gmail didn’t accept the message', detail: job.lastError || err?.message || 'Nothing was sent.', retryable: true };
      }
      return { code: code || 'error', title: 'Couldn’t send', detail: err?.message || 'Something went wrong.', retryable: true };
  }
}

export function newClientToken() {
  // eslint-disable-next-line no-undef
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const hex = '0123456789abcdef';
  let s = '';
  for (let i = 0; i < 36; i++) {
    if (i === 8 || i === 13 || i === 18 || i === 23) s += '-';
    else if (i === 14) s += '4';
    else s += hex[Math.floor(Math.random() * 16)];
  }
  return s;
}

// Window-level signal so the left-nav badge and dashboard can refetch after
// a reply/resolve without a route change.
export const NEEDS_REPLY_EVENT = 'nexsav:needs-reply-changed';
export function announceNeedsReplyChange() {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try { window.dispatchEvent(new Event(NEEDS_REPLY_EVENT)); } catch { /* noop */ }
  }
}
